// src/utils/exerciseUtils.js

export const isCardioExercise = (exOrItem, allExercises = []) => {
  if (!exOrItem) return false;
  
  // Muscle check helper
  const checkMuscles = (musclesVal) => {
    if (!musclesVal) return false;
    const arr = Array.isArray(musclesVal) ? musclesVal : [musclesVal];
    return arr.some(m => typeof m === 'string' && m.toLowerCase().includes('cardio'));
  };

  // 1. Match from the central exercise database first (most reliable source of truth)
  if (allExercises && allExercises.length > 0) {
    const exDef = allExercises.find(e => e.id === exOrItem.exerciseId || e.name === exOrItem.name);
    if (exDef) {
      if (checkMuscles(exDef.muscles) || checkMuscles(exDef.muscle)) return true;
    }
  }

  // 2. Fallback to check the item itself
  if (checkMuscles(exOrItem.muscles) || checkMuscles(exOrItem.muscle)) return true;

  return false;
};

export const getCardioType = (exerciseName = '') => {
  const name = exerciseName.toLowerCase();
  if (name.includes('tapis') || name.includes('course') || name.includes('marche')) return 'treadmill';
  if (name.includes('vélo') || name.includes('velo') || name.includes('bike') || name.includes('spinning')) return 'bike';
  if (name.includes('rameur') || name.includes('rowing machine')) return 'rower';
  if (name.includes('escalier') || name.includes('stair')) return 'stairs';
  if (name.includes('elliptique')) return 'elliptical';
  return 'generic';
};

export const getCardioFields = (cardioType) => {
  switch (cardioType) {
    case 'treadmill':
      return [
        { key: 'duration', label: 'Durée', placeholder: 'min', type: 'number', step: '0.5' },
        { key: 'distance', label: 'Distance', placeholder: 'km', type: 'number', step: '0.1' },
        { key: 'incline', label: 'Inclin.', placeholder: '%', type: 'number', step: '1' },
        { key: 'speed', label: 'Vitesse', placeholder: 'km/h', type: 'number', step: '0.1' }
      ];
    case 'bike':
    case 'elliptical':
      return [
        { key: 'duration', label: 'Durée', placeholder: 'min', type: 'number', step: '0.5' },
        { key: 'distance', label: 'Distance', placeholder: 'km', type: 'number', step: '0.1' },
        { key: 'intensity', label: 'Intensité', placeholder: 'lvl', type: 'number', step: '1' }
      ];
    case 'rower':
      return [
        { key: 'duration', label: 'Durée', placeholder: 'min', type: 'number', step: '0.5' },
        { key: 'distance', label: 'Distance', placeholder: 'm', type: 'number', step: '10' }
      ];
    case 'stairs':
      return [
        { key: 'duration', label: 'Durée', placeholder: 'min', type: 'number', step: '0.5' },
        { key: 'floors', label: 'Étages', placeholder: 'nb', type: 'number', step: '1' },
        { key: 'intensity', label: 'Vitesse', placeholder: 'lvl', type: 'number', step: '1' }
      ];
    default:
      return [
        { key: 'duration', label: 'Durée', placeholder: 'min', type: 'number', step: '0.5' },
        { key: 'distance', label: 'Distance', placeholder: 'km', type: 'number', step: '0.1' }
      ];
  }
};

export const formatSetDisplay = (set, isCardio = false, cardioType = 'generic') => {
  if (!set) return '';
  if (isCardio || set.isCardio) {
    const dur = set.duration !== undefined && set.duration !== '' ? set.duration : (set.reps || 0);
    let res = `${dur} min`;
    
    if (cardioType === 'stairs') {
      const floors = set.floors !== undefined && set.floors !== '' ? set.floors : '';
      if (floors) res += ` • ${floors} nb`;
    } else {
      const dist = set.distance !== undefined && set.distance !== '' ? set.distance : (set.weight || '');
      const distUnit = cardioType === 'rower' ? 'm' : 'km';
      if (dist && parseFloat(dist) > 0) res += ` • ${dist} ${distUnit}`;
    }

    if (cardioType === 'treadmill') {
      if (set.incline !== undefined && set.incline !== '') res += ` • Inc ${set.incline}%`;
      if (set.speed !== undefined && set.speed !== '') res += ` • ${set.speed} km/h`;
    }
    if ((cardioType === 'bike' || cardioType === 'elliptical' || cardioType === 'stairs') && set.intensity !== undefined && set.intensity !== '') {
      res += ` • lvl ${set.intensity}`;
    }

    if (set.calories && parseFloat(set.calories) > 0) {
      res += ` • ${set.calories} kcal`;
    }
    return res;
  }

  if (set.isUnilateral) {
    const wL = (set.weightL !== undefined && set.weightL !== null && set.weightL !== '') ? set.weightL : 0;
    const wR = (set.weightR !== undefined && set.weightR !== null && set.weightR !== '') ? set.weightR : 0;
    const rL = (set.repsL !== undefined && set.repsL !== null && set.repsL !== '') ? set.repsL : 0;
    const rR = (set.repsR !== undefined && set.repsR !== null && set.repsR !== '') ? set.repsR : 0;

    if (wL === wR) {
      return `G: ${rL} / D: ${rR} reps à ${wL}kg`;
    }
    return `G: ${rL} reps à ${wL}kg • D: ${rR} reps à ${wR}kg`;
  }

  const reps = (set.reps !== undefined && set.reps !== null && set.reps !== '') ? set.reps : 0;
  const weight = (set.weight !== undefined && set.weight !== null && set.weight !== '') ? set.weight : 0;
  return `${reps} reps à ${weight}kg`;
};



export const MAX_SET_REPS = 100;
export const MAX_SET_WEIGHT = 500;
export const MAX_WORKOUT_VOLUME = 50000;

/**
 * Calcule le tonnage / volume total officiel d'une séance de musculation :
 * - Répétitions : si décimal (ex: 6.5 reps), on prend l'entier juste en dessous (Math.floor).
 * - Poids : prend en compte les décimales (ex: 2.5 kg).
 * - Échauffements : exclus par défaut (s.isWarmup).
 * - Unilatéral : (weightL * repsL) + (weightR * repsR).
 * - Cardio : les exercices de pur cardio ne génèrent pas de tonnage de musculation.
 * - Supersets et Dropsets inclus.
 */
export const calculateWorkoutVolume = (workout, options = {}) => {
  if (!workout) return 0;
  if (typeof workout === 'number') {
    return Math.min(Math.max(0, Math.round(workout)), options.maxWorkout || MAX_WORKOUT_VOLUME);
  }

  const {
    excludeWarmup = true,
    maxReps = MAX_SET_REPS,
    maxWeight = MAX_SET_WEIGHT,
    maxWorkout = MAX_WORKOUT_VOLUME,
    allExercises = []
  } = options;

  let total = 0;

  const processSet = (s) => {
    if (!s) return;
    if (excludeWarmup && s.isWarmup) return;

    if (s.isUnilateral) {
      const rawWL = parseFloat(s.weightL) || 0;
      const rawRL = Math.floor(parseFloat(s.repsL) || 0); // Demi-reps: entier juste en dessous
      const wL = Math.min(Math.max(0, rawWL), maxWeight);
      const rL = Math.min(Math.max(0, rawRL), maxReps);

      const rawWR = parseFloat(s.weightR) || 0;
      const rawRR = Math.floor(parseFloat(s.repsR) || 0); // Demi-reps: entier juste en dessous
      const wR = Math.min(Math.max(0, rawWR), maxWeight);
      const rR = Math.min(Math.max(0, rawRR), maxReps);

      total += (wL * rL) + (wR * rR);
    } else {
      const rawW = parseFloat(s.weight) || 0;
      const rawR = Math.floor(parseFloat(s.reps) || 0); // Demi-reps: entier juste en dessous
      const w = Math.min(Math.max(0, rawW), maxWeight);
      const r = Math.min(Math.max(0, rawR), maxReps);

      total += (w * r);
    }
  };

  if (Array.isArray(workout.exercises)) {
    workout.exercises.forEach(block => {
      if (isCardioExercise(block, allExercises)) return;

      if (block.type === 'superset' && Array.isArray(block.items)) {
        block.items.forEach(item => {
          if (!isCardioExercise(item, allExercises) && Array.isArray(item?.sets)) {
            item.sets.forEach(processSet);
          }
        });
      } else if (block.type === 'dropset' && Array.isArray(block.dropsetRuns)) {
        block.dropsetRuns.forEach(run => {
          if (Array.isArray(run)) run.forEach(processSet);
        });
      } else if (Array.isArray(block.sets)) {
        block.sets.forEach(processSet);
      }
    });
  } else if (Array.isArray(workout.sets)) {
    workout.sets.forEach(processSet);
  }

  return Math.min(Math.round(total), maxWorkout);
};

export const MAX_WORKOUT_BOSS_REPS = 5000;

/**
 * Calcule le nombre total officiel de répétitions d'une séance (utilisé notamment contre les boss sensibles aux répétitions comme Le Barromancien) :
 * - Répétitions : si décimal (ex: 6.5 reps), on prend l'entier juste en dessous (Math.floor).
 * - Échauffements : exclus par défaut (s.isWarmup).
 * - Unilatéral : repsL + repsR.
 * - Supersets et Dropsets inclus.
 * - Cardio : exclu si pas de répétitions de musculation.
 */
export const calculateWorkoutReps = (workout, options = {}) => {
  if (!workout) return 0;
  if (typeof workout === 'number') {
    return Math.min(Math.max(0, Math.round(workout)), options.maxWorkout || MAX_WORKOUT_BOSS_REPS);
  }

  const {
    excludeWarmup = true,
    maxReps = MAX_SET_REPS,
    maxWorkout = MAX_WORKOUT_BOSS_REPS,
    allExercises = []
  } = options;

  let total = 0;

  const processSet = (s) => {
    if (!s) return;
    if (excludeWarmup && s.isWarmup) return;

    if (s.isUnilateral) {
      const rawRL = Math.floor(parseFloat(s.repsL) || 0); // Demi-reps: entier juste en dessous
      const rawRR = Math.floor(parseFloat(s.repsR) || 0);
      const rL = Math.min(Math.max(0, rawRL), maxReps);
      const rR = Math.min(Math.max(0, rawRR), maxReps);
      total += (rL + rR);
    } else {
      const rawR = Math.floor(parseFloat(s.reps) || 0); // Demi-reps: entier juste en dessous
      const r = Math.min(Math.max(0, rawR), maxReps);
      total += r;
    }
  };

  if (Array.isArray(workout.exercises)) {
    workout.exercises.forEach(block => {
      if (isCardioExercise(block, allExercises)) return;

      if (block.type === 'superset' && Array.isArray(block.items)) {
        block.items.forEach(item => {
          if (!isCardioExercise(item, allExercises) && Array.isArray(item?.sets)) {
            item.sets.forEach(processSet);
          }
        });
      } else if (block.type === 'dropset' && Array.isArray(block.dropsetRuns)) {
        block.dropsetRuns.forEach(run => {
          if (Array.isArray(run)) run.forEach(processSet);
        });
      } else if (Array.isArray(block.sets)) {
        block.sets.forEach(processSet);
      }
    });
  } else if (Array.isArray(workout.sets)) {
    workout.sets.forEach(processSet);
  }

  return Math.min(Math.round(total), maxWorkout);
};

export const MAX_WORKOUT_DURATION_MINUTES = 180; // Plafond 3h max par séance pour éviter les chronos oubliés

/**
 * Calcule les dégâts d'une séance basés sur la durée active de l'entraînement :
 * - Règle convenue : 1h30 (90 minutes) = 1 300 dégâts.
 * - Formule : Math.round(durationMinutes * (1300 / 90))
 * - Plafond de sécurité : max 180 minutes (2 600 dégâts max par séance).
 */
export const calculateWorkoutDurationDamage = (workout, options = {}) => {
  if (!workout) return 0;
  let duration = 0;
  if (typeof workout === 'number') {
    duration = workout;
  } else if (workout.duration !== undefined && workout.duration !== null) {
    duration = parseFloat(workout.duration) || 0;
  }
  const maxMinutes = options.maxDuration || MAX_WORKOUT_DURATION_MINUTES;
  const safeDuration = Math.min(Math.max(0, duration), maxMinutes);
  return Math.round(safeDuration * (1300 / 90));
};
